import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';

import { DELETE_BULK_VOUCHERS } from 'store/mutations';
import { GET_BULK_VOUCHERS_METADATA } from 'store/queries';
import AgentPortalError from 'errors/AgentPortalError';
import { FormattedMessage } from 'react-intl';
import { ActionButton } from 'components/ui/Button';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import StyledCalendar from 'components/ui/StyledCalendar';
import { BULK_VOUCHERS_STATUS } from 'constants/vouchers';
import PROCESS_BULK_VOUCHERS from 'store/mutations/PROCESS_BULK_VOUCHERS';
import { downloadBulkVouchers } from 'utils/bulkVouchers';

import {
  ErrorField,
  Field,
  MandatoryFieldLabel,
  StyledActionButtonContainers,
  StyledContainer,
  StyledForm,
  StyledInput,
  StyledLink,
  StyledMetadataContainer,
  StyledMetadataLabel,
  StyledMetadataRow,
  StyledMetadataValue,
  StyledTitle,
  StyledTravelDate
} from './ProcessBulkVouchers.styles';

const ProcessBulkVouchers = ({ user }) => {
  const [vouchersMetadata, setVouchersMetadata] = useState(null);

  const history = useHistory();
  const { register, handleSubmit, formState, setValue, watch, reset } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      serviceNumber: '',
      travelDate: moment().subtract(1, 'day').toDate()
    }
  });

  const [
    getBulkVouchersMetadata,
    {
      loading: loadingGetBulkVouchersMetadata,
      error: errorGetBulkVouchersMetadata,
      called: calledGetBulkVouchersMetadata,
      startPolling: startMetadataPolling,
      stopPolling: stopMetadataPolling,
      data: dataGetBulkVouchersMetadata
    }
  ] = useLazyQuery(GET_BULK_VOUCHERS_METADATA, {
    fetchPolicy: 'network-only'
  });

  const [deleteBulkVouchers, { loading: loadingDeleteBulkVouchers, error: errorDeleteBulkVouchers }] = useMutation(DELETE_BULK_VOUCHERS, {
    onCompleted: () => {
      setVouchersMetadata(null);
    }
  });

  const [processBulkVouchers, { loading: loadingProcessBulkVouchers, error: errorProcessBulkVouchers }] = useMutation(
    PROCESS_BULK_VOUCHERS,
    {
      onCompleted: () => {
        getBulkVouchersMetadata({
          variables: {
            serviceNumber: vouchersMetadata.serviceNumber,
            travelDate: vouchersMetadata.travelDate
          }
        });
      }
    }
  );

  useEffect(() => {
    const metadata = dataGetBulkVouchersMetadata?.getBulkVouchersMetadata;

    if (metadata && JSON.stringify(metadata) !== JSON.stringify(vouchersMetadata)) {
      setVouchersMetadata(metadata);
    }

    if (metadata?.status === BULK_VOUCHERS_STATUS.READY || metadata?.status === BULK_VOUCHERS_STATUS.FAILED) {
      stopMetadataPolling();
    }

    if (metadata?.status === BULK_VOUCHERS_STATUS.IN_PROGRESS) {
      startMetadataPolling(5000);
    }
  }, [dataGetBulkVouchersMetadata?.getBulkVouchersMetadata, startMetadataPolling, stopMetadataPolling, vouchersMetadata]);

  const maxTraveDate = moment().subtract(1, 'day').toDate();
  const travelDate = watch('travelDate');
  const { errors } = formState;

  const error = errorGetBulkVouchersMetadata || errorDeleteBulkVouchers || errorProcessBulkVouchers;
  const loading = loadingGetBulkVouchersMetadata || loadingDeleteBulkVouchers || loadingProcessBulkVouchers;

  useEffect(() => {
    if (!user.canManageProcessBulkVouchers) {
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleGetBulkVouchersMetadata = ({ serviceNumber, travelDate }) =>
    getBulkVouchersMetadata({
      variables: {
        serviceNumber,
        travelDate: moment(travelDate).format('YYYY-MM-DD')
      }
    });

  const handleDeleteBulkVouchers = () => {
    reset();
    deleteBulkVouchers({
      variables: {
        serviceNumber: vouchersMetadata.serviceNumber,
        travelDate: vouchersMetadata.travelDate
      }
    });
  };

  const handleProcessBulkVouchers = () => {
    processBulkVouchers({
      variables: {
        serviceNumber: vouchersMetadata.serviceNumber,
        travelDate: vouchersMetadata.travelDate
      }
    });
  };

  const handleSetTravelDate = (value) => {
    setValue('travelDate', value);
  };

  const downloadFile = (filename) => (event) => {
    event.preventDefault();
    downloadBulkVouchers(filename);
  };

  if (error) {
    throw new AgentPortalError(error);
  }

  return (
    <StyledContainer>
      <StyledTitle>
        <FormattedMessage id="bulkVouchers.processTitle" />
      </StyledTitle>
      <StyledForm data-testid="process-bulk-vouchers-form" onSubmit={handleSubmit(handleGetBulkVouchersMetadata)}>
        <Field>
          <MandatoryFieldLabel htmlFor="travelDate">
            <FormattedMessage id="bulkVouchers.travelDate" />
          </MandatoryFieldLabel>
          <StyledTravelDate>{moment(travelDate).format('dddd DD MMMM YYYY')}</StyledTravelDate>
          <StyledCalendar
            {...register('travelDate', { required: 'dateError' })}
            name="travelDate"
            showNeighboringMonth={false}
            tabIndex="-1"
            maxDate={maxTraveDate}
            value={travelDate}
            onChange={handleSetTravelDate}
          />
          {errors.travelDate && (
            <ErrorField id="error-travelDate" data-testid="error-travelDate">
              <FormattedMessage id="bulkVouchers.serviceNumberValidationMessage" />
            </ErrorField>
          )}
        </Field>
        <Field>
          <MandatoryFieldLabel htmlFor="serviceNumber">
            <FormattedMessage id="bulkVouchers.serviceNumber" />
          </MandatoryFieldLabel>
          <StyledInput
            {...register('serviceNumber', { required: true })}
            id="serviceNumber"
            type="number"
            data-cy="serviceNumber-input"
            data-testid="serviceNumber-input"
          />
          {errors.serviceNumber && (
            <ErrorField id="error-serviceNumber" data-testid="error-serviceNumber">
              <FormattedMessage id="bulkVouchers.serviceNumberValidationMessage" />
            </ErrorField>
          )}
        </Field>
        {vouchersMetadata && vouchersMetadata?.status ? (
          <StyledMetadataContainer data-testid="bulk-vouchers-metadata-container" data-cy="bulk-vouchers-metadata-container">
            <StyledMetadataRow>
              <StyledMetadataLabel>
                <FormattedMessage id="bulkVouchers.status" />:
              </StyledMetadataLabel>
              <FormattedMessage id={`bulkVouchers.status.${vouchersMetadata.status}`} />
            </StyledMetadataRow>
            <StyledMetadataRow>
              <StyledMetadataLabel>
                <FormattedMessage id="bulkVouchers.selectedTickets" />:
              </StyledMetadataLabel>
              <StyledMetadataValue>
                {vouchersMetadata.selectedTotalCount}
                {vouchersMetadata.selectedTotalCount > 0 ? (
                  <>
                    {' '}
                    -{' '}
                    <StyledLink type="button" onClick={downloadFile(vouchersMetadata.selectedFile)}>
                      {vouchersMetadata.selectedFile}
                    </StyledLink>
                  </>
                ) : (
                  ''
                )}
              </StyledMetadataValue>
            </StyledMetadataRow>
            <StyledMetadataRow>
              <StyledMetadataLabel>
                <FormattedMessage id="bulkVouchers.ignoredTickets" />:
              </StyledMetadataLabel>
              <StyledMetadataValue>
                {vouchersMetadata.ignoredTotalCount}
                {vouchersMetadata.ignoredTotalCount > 0 ? (
                  <>
                    {' '}
                    -{' '}
                    <StyledLink type="button" onClick={downloadFile(vouchersMetadata.ignoredFile)}>
                      {vouchersMetadata.ignoredFile}
                    </StyledLink>
                  </>
                ) : (
                  ''
                )}
              </StyledMetadataValue>
            </StyledMetadataRow>
            <StyledMetadataRow>
              <StyledMetadataLabel>
                <FormattedMessage id="bulkVouchers.vouchersType" />:
              </StyledMetadataLabel>
              <StyledMetadataValue>
                <FormattedMessage
                  id="bulkVouchers.vouchersTypePercentage"
                  values={{ voucherType: vouchersMetadata.voucherType, percentage: vouchersMetadata.compensationPercentage }}
                />
              </StyledMetadataValue>
            </StyledMetadataRow>
            {vouchersMetadata.selectedTotalPriceAmount.length > 0 && (
              <StyledMetadataRow>
                <StyledMetadataLabel>
                  <FormattedMessage id="bulkVouchers.selectedCompensationAmount" />:
                </StyledMetadataLabel>
                <StyledMetadataValue>
                  {vouchersMetadata.selectedTotalPriceAmount.reduce((acc, { amount, currency }, index, array) => {
                    acc.push(<FormattedCurrency key={currency + amount} amount={amount} currency={currency} />);

                    if (index < array.length - 1) {
                      acc.push(', ');
                    }
                    return acc;
                  }, [])}
                </StyledMetadataValue>
              </StyledMetadataRow>
            )}
            {vouchersMetadata.successFile && (
              <StyledMetadataRow>
                <StyledMetadataLabel>
                  <FormattedMessage id="bulkVouchers.successTickets" />:
                </StyledMetadataLabel>
                <StyledMetadataValue>
                  {vouchersMetadata.successTotalCount}
                  {vouchersMetadata.successTotalCount > 0 ? (
                    <>
                      {' '}
                      -{' '}
                      <StyledLink type="button" onClick={downloadFile(vouchersMetadata.successFile)}>
                        {vouchersMetadata.successFile}
                      </StyledLink>
                    </>
                  ) : (
                    ''
                  )}
                </StyledMetadataValue>
              </StyledMetadataRow>
            )}
            {vouchersMetadata.failedFile && (
              <StyledMetadataRow>
                <StyledMetadataLabel>
                  <FormattedMessage id="bulkVouchers.failedTickets" />:
                </StyledMetadataLabel>
                <StyledMetadataValue>
                  {vouchersMetadata.failedTotalCount}
                  {vouchersMetadata.failedTotalCount > 0 ? (
                    <>
                      {' '}
                      -{' '}
                      <StyledLink type="button" onClick={downloadFile(vouchersMetadata.failedFile)}>
                        {vouchersMetadata.failedFile}
                      </StyledLink>
                    </>
                  ) : (
                    ''
                  )}
                </StyledMetadataValue>
              </StyledMetadataRow>
            )}
          </StyledMetadataContainer>
        ) : (
          calledGetBulkVouchersMetadata && (
            <ErrorField id="error-notFoundMetadata" data-testid="error-notFoundMetadata">
              <FormattedMessage id="bulkVouchers.notFoundMetadataMessage" />
            </ErrorField>
          )
        )}
        <StyledActionButtonContainers data-testid="bulk-vouchers-action-buttons">
          <ActionButton data-cy="search-by-details" data-testid="search-by-details" type="submit" disabled={loading} loading={loading}>
            <FormattedMessage id="bulkVouchers.search" />
          </ActionButton>
          {vouchersMetadata?.status && vouchersMetadata.status === BULK_VOUCHERS_STATUS.READY && (
            <ActionButton
              data-cy="delete-bulk-vouchers"
              data-testid="delete-bulk-vouchers"
              onClick={handleDeleteBulkVouchers}
              disabled={loading}
              loading={loading}
            >
              <FormattedMessage id="bulkVouchers.delete" />
            </ActionButton>
          )}
          {vouchersMetadata?.status &&
            (vouchersMetadata.status === BULK_VOUCHERS_STATUS.READY || vouchersMetadata.status === BULK_VOUCHERS_STATUS.FAILED) && (
              <ActionButton
                data-cy="process-bulk-vouchers"
                data-testid="process-bulk-vouchers"
                onClick={handleProcessBulkVouchers}
                disabled={loading}
                loading={loading}
              >
                <FormattedMessage id="bulkVouchers.process" />
              </ActionButton>
            )}
        </StyledActionButtonContainers>
      </StyledForm>
    </StyledContainer>
  );
};

ProcessBulkVouchers.propTypes = {
  user: PropTypes.shape({
    canManageProcessBulkVouchers: PropTypes.bool
  }),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  })
};

export default ProcessBulkVouchers;
